import { Injectable } from '@angular/core';
import { StateToken, State, StateContext, Selector, Action } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { DepartmentGroupState } from 'src/app/department/state/department-group.state';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IDepartmentGroupModel } from 'src/app/department/models/department-group.model';
import { IDepartmentInfo } from 'src/app/shared/user-view/models/tenant-user-access.model';
import {
  IApiResponseGroupStandardJobTemplates,
  IStandardJobTemplateGroupResponse,
  IStandardJobTemplateResponse,
  IStandardJobTemplateForTable,
  IStandardJobTemplateFromList,
} from '../models/standard-job-template.model';
import { StandardJobTemplateApiService } from '../services/standard-job-template-api.service';
import {
  LoadStandardJobTemplates,
  LoadAllStandardJobTemplates,
  LoadStandardJobTemplateById,
  LoadStandardJobTemplateVersionById,
  CreateStandardJobTemplate,
  UpdateStandardJobTemplate,
  DeleteStandardJobTemplate,
} from './standard-job-template.actions';
import { AdminMaintenanceRoutes } from '../../core/enums/routes.enum';

export interface StandardJobTemplateStateModel {
  standardJobTemplates: IStandardJobTemplateForTable[];
  standardJobTemplatesGroups: IStandardJobTemplateGroupResponse[];
  currentStandardJobTemplate: IStandardJobTemplateResponse;
  currentStandardJobTemplateVersion: IStandardJobTemplateResponse;
  allStandardJobTemplates: IStandardJobTemplateFromList[];
  standardJobTemplatesTotalPages: number;
}

const JOB_TYPE_STATE_TOKEN = new StateToken<StandardJobTemplateStateModel>('standardJobTemplate');

@State<StandardJobTemplateStateModel>({
  name: JOB_TYPE_STATE_TOKEN,
  defaults: {
    standardJobTemplates: [],
    standardJobTemplatesGroups: [],
    currentStandardJobTemplate: <IStandardJobTemplateResponse>{},
    currentStandardJobTemplateVersion: <IStandardJobTemplateResponse>{},
    allStandardJobTemplates: [],
    standardJobTemplatesTotalPages: 0,
  },
})
@Injectable()
export class StandardJobTemplateState {
  constructor(private service: StandardJobTemplateApiService) {}

  @Action(LoadStandardJobTemplates)
  loadStandardJobTemplates(
    { getState, setState }: StateContext<StandardJobTemplateStateModel>,
    { filter }: LoadStandardJobTemplates
  ) {
    return this.service.loadStandardJobTemplates(filter).pipe(
      tap((response: IApiResponseGroupStandardJobTemplates) => {
        const state = getState();
        setState({
          ...state,
          standardJobTemplates: response.data,
          standardJobTemplatesGroups: response.groupData,
          standardJobTemplatesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllStandardJobTemplates, { cancelUncompleted: true })
  loadAllStandardJobTemplates(ctx: StateContext<StandardJobTemplateStateModel>) {
    return this.service.loadAllStandardJobTemplates().pipe(
      tap((allStandardJobTemplates: IStandardJobTemplateFromList[]) => {
        ctx.setState(patch({ allStandardJobTemplates }));
      })
    );
  }

  @Action(LoadStandardJobTemplateById, { cancelUncompleted: true })
  loadStandardJobTemplateById(
    { getState, setState }: StateContext<StandardJobTemplateStateModel>,
    { id }: LoadStandardJobTemplateById
  ) {
    return this.service.loadStandardJobTemplateById(id).pipe(
      tap((response: IStandardJobTemplateResponse) => {
        const state = getState();
        setState({
          ...state,
          currentStandardJobTemplate: response,
        });
      })
    );
  }

  @Action(LoadStandardJobTemplateVersionById, { cancelUncompleted: true })
  loadStandardJobTemplateVersionById(
    { getState, setState }: StateContext<StandardJobTemplateStateModel>,
    { id, versionId }: LoadStandardJobTemplateVersionById
  ) {
    return this.service.loadStandardJobTemplateVersionById(id, versionId).pipe(
      tap((response: IStandardJobTemplateResponse) => {
        const state = getState();
        setState({
          ...state,
          currentStandardJobTemplateVersion: response,
        });
      })
    );
  }

  @Action(CreateStandardJobTemplate)
  @OnSuccess({
    message: 'standardJobTemplateAdded',
  })
  createStandardJobTemplate(
    { setState }: StateContext<StandardJobTemplateStateModel>,
    { standardJobTemplate }: CreateStandardJobTemplate
  ) {
    return this.service.createStandardJobTemplate(standardJobTemplate).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${AdminMaintenanceRoutes.StandardJobTemplatesRoot}/${standardJobTemplate.menuId}`
        );
      })
    );
  }

  @Action(UpdateStandardJobTemplate)
  @OnSuccess({
    message: 'standardJobTemplateUpdated',
  })
  updateStandardJobTemplate(
    { setState }: StateContext<StandardJobTemplateStateModel>,
    { standardJobTemplate }: UpdateStandardJobTemplate
  ) {
    return this.service.updateStandardJobTemplate(standardJobTemplate).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${AdminMaintenanceRoutes.StandardJobTemplatesRoot}/${standardJobTemplate.menuId}`
        );
      })
    );
  }

  @Action(DeleteStandardJobTemplate, { cancelUncompleted: true })
  @OnSuccess({
    message: 'standardJobTemplateDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteStandardJobTemplate(
    { getState }: StateContext<StandardJobTemplateStateModel>,
    { id }: DeleteStandardJobTemplate
  ) {
    return this.service.deleteStandardJobTemplate(id).pipe(
      tap(() => {
        const menuId = getState().currentStandardJobTemplate.menu.id;
        redirectAndBypassUnsavedDataCheck(
          `${AdminMaintenanceRoutes.StandardJobTemplatesRoot}/${menuId}`
        );
      })
    );
  }

  @Selector()
  static getStandardJobTemplatesTotalPages(state: StandardJobTemplateStateModel) {
    return state.standardJobTemplatesTotalPages;
  }

  @Selector()
  static getStandardJobTemplates(state: StandardJobTemplateStateModel) {
    return state.standardJobTemplates;
  }

  @Selector([
    StandardJobTemplateState.getStandardJobTemplates,
    DepartmentGroupState.getAllDepartmentGroups,
    TenantUserAccessState.currentDepartment,
  ])
  static getStandardJobTemplatesForCurrentDepartment(
    templates: IStandardJobTemplateForTable[],
    departmentGroups: IDepartmentGroupModel[],
    currentDepartment: IDepartmentInfo
  ) {
    const departmentGroupsForDepartment = departmentGroups.filter((group) =>
      group.departments.includes(currentDepartment.name)
    );

    return templates.filter(
      (template) =>
        template.departments.some((d) => d.id === getDepartmentId()) ||
        template.departmentGroups.some((g) =>
          departmentGroupsForDepartment.some((dg) => dg.id === g.id)
        )
    );
  }

  @Selector()
  static getStandardJobTemplatesGroups(state: StandardJobTemplateStateModel) {
    return state.standardJobTemplatesGroups;
  }

  @Selector()
  static getAllStandardJobTemplates(
    state: StandardJobTemplateStateModel
  ): IStandardJobTemplateFromList[] {
    return state.allStandardJobTemplates;
  }

  @Selector()
  static getStandardJobTemplateById(state: StandardJobTemplateStateModel) {
    return state.currentStandardJobTemplate;
  }

  @Selector()
  static getCurrentStandardJobTemplateVersionById(state: StandardJobTemplateStateModel) {
    return state.currentStandardJobTemplateVersion;
  }
}
